import React from "react";

function formatNumber(value, format) {
  switch (format) {
    case "dollars":
      return `$${parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;
    case "percentage":
      return `${(value * 100).toFixed(1)}%`;
    case "number":
      return `${parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      })}`;
    default:
      return value.toFixed(1);
  }
}

function getMetricLabel(metricValue, metrics) {
  if (!metricValue || !metrics || !Array.isArray(metrics)) {
    return metricValue || "Unknown";
  }

  let baseValue = metricValue;
  let suffix = "";

  if (metricValue.endsWith("_previous_year")) {
    baseValue = metricValue.replace("_previous_year", "");
    suffix = " (Prev Year)";
  }

  const metric = metrics.find((m) => m && m.value === baseValue);
  if (metric && metric.label) {
    return `${metric.label}${suffix}`;
  }
  return metricValue;
}

const GaugeChart = ({
  selectedMetrics,
  metrics,
  summaryData,
  isDashboard,
}) => {
  const safeMetrics = metrics || [];
  const safeSelectedMetrics = selectedMetrics || [];
  const safeSummaryData = summaryData || [];

  const firstMetric = safeMetrics.find((m) => m && m.value === safeSelectedMetrics[0]);
  const formatType = firstMetric && firstMetric.format ? firstMetric.format : "number";

  if (!safeSummaryData.length || !safeSelectedMetrics.length) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No data available for gauge</p>
      </div>
    );
  }

  const firstMetricData = safeSummaryData[0];
  const value = firstMetricData.total || firstMetricData.average || 0;
  const label = getMetricLabel(firstMetricData.metric, safeMetrics);

  // Calculate gauge parameters
  let maxValue = value * 1.5; // Set max to 150% of current value
  let targetValue = value * 1.2; // Set target to 120% of current value

  // For percentages, cap at 100%
  if (formatType === "percentage") {
    maxValue = Math.max(1, value * 1.2);
    targetValue = 0.9; // 90% target for percentage metrics
  }

  const percentage = Math.min((value / maxValue) * 100, 100);
  const targetPercentage = Math.min((targetValue / maxValue) * 100, 100);

  // Color based on performance vs target
  let gaugeColor = "#dc2626"; // Red
  if (value >= targetValue) {
    gaugeColor = "#059669"; // Green
  } else if (value >= targetValue * 0.8) {
    gaugeColor = "#d97706"; // Orange
  }

  const size = isDashboard ? 200 : 250;
  const strokeWidth = 20;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * Math.PI;
  const strokeDasharray = `${circumference} ${circumference}`;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="flex flex-col items-center justify-center h-full p-4">
      <div className="relative" style={{ width: size, height: size }}>
        {/* Background circle */}
        <svg
          width={size}
          height={size}
          className="transform -rotate-90"
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
            fill="none"
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={gaugeColor}
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
            style={{
              strokeDasharray,
              strokeDashoffset,
              transition: "stroke-dashoffset 0.5s ease-in-out",
            }}
          />
          {/* Target indicator */}
          {targetPercentage < 100 && (
            <circle
              cx={size / 2 + radius * Math.cos((targetPercentage / 100) * 2 * Math.PI - Math.PI / 2)}
              cy={size / 2 + radius * Math.sin((targetPercentage / 100) * 2 * Math.PI - Math.PI / 2)}
              r="4"
              fill="#374151"
            />
          )}
        </svg>

        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="text-2xl font-bold text-gray-900">
            {formatNumber(value, formatType)}
          </div>
          <div className="text-sm text-gray-500 mt-1">
            {percentage.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Label and legend */}
      <div className="mt-4 text-center">
        <div className="text-sm font-medium text-gray-900 mb-2">
          {label}
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-gray-300"></div>
            <span>Max: {formatNumber(maxValue, formatType)}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-gray-600"></div>
            <span>Target: {formatNumber(targetValue, formatType)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GaugeChart;
